import { useEffect, useState } from 'react'
import { Table, Select, Space, Button, Typography, Modal, Input, Tag } from 'antd'
import { useSearchParams } from 'react-router-dom'
import {
  useSlaEventList,
  useRemindSlaEvent,
  useIgnoreSlaEvent,
  useResolveSlaEvent,
} from '@/modules/sla/queries'
import StatusTag from '@/components/feedback/StatusTag'
import ErrorResult from '@/components/feedback/ErrorResult'
import { getSlaRuleName, getSlaTargetRoleLabel } from '@/modules/sla/labels'
import SlaEventDetailDrawer from './components/SlaEventDetailDrawer'
import type { SlaEventItem } from '@/types/sla'
import { formatShortDateTime } from '@/utils/datetime'
import { useAuthStore } from '@/stores/authStore'

const { Text } = Typography
const { TextArea } = Input

export default function SlaEventListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const { data, isLoading, isError, error, refetch } = useSlaEventList({
    page,
    page_size: pageSize,
    event_status: statusFilter,
  })

  const remindEvent = useRemindSlaEvent()
  const ignoreEvent = useIgnoreSlaEvent()
  const resolveEvent = useResolveSlaEvent()
  const hasPermission = useAuthStore((s) => s.hasPermission)
  const canRemindSla = hasPermission('sla:remind')
  const canCloseSla = hasPermission('sla:ignore')

  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false)
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [detailEventId, setDetailEventId] = useState<string | null>(null)
  const [ignoreReason, setIgnoreReason] = useState('')
  const [resolveNote, setResolveNote] = useState('')

  useEffect(() => {
    const eventId = searchParams.get('eventId')
    if (eventId) setDetailEventId(eventId)
  }, [searchParams])

  function openDetail(eventId: string) {
    const next = new URLSearchParams(searchParams)
    next.set('eventId', eventId)
    setSearchParams(next, { replace: true })
    setDetailEventId(eventId)
  }

  function closeDetail() {
    const next = new URLSearchParams(searchParams)
    next.delete('eventId')
    setSearchParams(next, { replace: true })
    setDetailEventId(null)
  }

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <Select
          placeholder="事件状态"
          allowClear
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          options={[
            { label: '待处理', value: 'open' },
            { label: '已提醒', value: 'reminded' },
            { label: '已忽略', value: 'ignored' },
            { label: '已解决', value: 'resolved' },
          ]}
        />
      </Space>

      <Table<SlaEventItem>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) },
        }}
        columns={[
          {
            title: '超时类型',
            dataIndex: 'rule_code',
            width: 150,
            render: (ruleCode: string, r: SlaEventItem) => getSlaRuleName(ruleCode, r.rule_name),
          },
          { title: '工单', dataIndex: 'task_title', ellipsis: true, render: (v: string) => v || <Text type="secondary">-</Text> },
          { title: '阶段', dataIndex: 'platform_status', width: 120, render: (v: string) => v || '-' },
          { title: '提醒对象', dataIndex: 'target_role', width: 110, render: (v: string) => getSlaTargetRoleLabel(v) },
          { title: '责任人', dataIndex: 'target_account', width: 80 },
          { title: '首次命中', dataIndex: 'first_detected_at', width: 120, render: (v: string) => formatShortDateTime(v) },
          {
            title: '升级',
            dataIndex: 'current_escalation_level',
            width: 80,
            render: (value: number) => (
              value >= 2 ? <Tag color="volcano">L2</Tag> : value === 1 ? <Tag color="gold">L1</Tag> : <Tag>未提醒</Tag>
            ),
          },
          {
            title: '最近提醒',
            dataIndex: 'last_reminded_at',
            width: 120,
            render: (v?: string | null) => v ? formatShortDateTime(v) : '-',
          },
          { title: '次数', dataIndex: 'remind_count', width: 50 },
          { title: '状态', dataIndex: 'event_status', width: 90, render: (v: string) => <StatusTag type="sla" status={v} /> },
          {
            title: '操作', width: 180, render: (_: unknown, r: SlaEventItem) => (
              <Space size="small">
                <Button type="link" size="small" onClick={() => openDetail(r.id)}>详情</Button>
                {(r.event_status === 'open' || r.event_status === 'reminded') && canRemindSla && (
                  <Button type="link" size="small" onClick={() => remindEvent.mutate({ eventId: r.id })}>提醒</Button>
                )}
                {r.event_status === 'open' && canCloseSla && (
                  <>
                    <Button type="link" size="small" danger onClick={() => { setSelectedEventId(r.id); setIgnoreModalOpen(true) }}>忽略</Button>
                  </>
                )}
                {(r.event_status === 'open' || r.event_status === 'reminded') && canCloseSla && (
                  <Button type="link" size="small" onClick={() => { setSelectedEventId(r.id); setResolveModalOpen(true) }}>解决</Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      {/* 忽略弹窗 */}
      <Modal title="忽略超时提醒" open={ignoreModalOpen}
        onOk={() => ignoreEvent.mutate(
          { eventId: selectedEventId!, reason: ignoreReason },
          { onSuccess: () => { setIgnoreModalOpen(false); setIgnoreReason('') } }
        )}
        onCancel={() => setIgnoreModalOpen(false)}
        confirmLoading={ignoreEvent.isPending}>
        <TextArea placeholder="忽略原因（必填）" value={ignoreReason} onChange={(e) => setIgnoreReason(e.target.value)} />
      </Modal>

      {/* 解决弹窗 */}
      <Modal title="标记解决" open={resolveModalOpen}
        onOk={() => resolveEvent.mutate(
          { eventId: selectedEventId!, note: resolveNote },
          { onSuccess: () => { setResolveModalOpen(false); setResolveNote('') } }
        )}
        onCancel={() => setResolveModalOpen(false)}
        confirmLoading={resolveEvent.isPending}>
        <TextArea placeholder="解决说明" value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} />
      </Modal>

      {/* 详情抽屉 */}
      <SlaEventDetailDrawer
        eventId={detailEventId}
        onClose={closeDetail}
      />
    </div>
  )
}
